import React, { ReactElement, useEffect, useMemo, useState } from "react";
import { useLocalization } from '@progress/kendo-react-intl';
import { enMessages } from "../../../../messages/en-US";
import moment from "moment";
import { SortDescriptor, process, State } from "@progress/kendo-data-query";
import { ExcelExport, ExcelExportColumn } from "@progress/kendo-react-excel-export";
import { Grid, GridColumn as Column, GridToolbar } from "@progress/kendo-react-grid";
import { CustomTextFilterCell } from "../../../Shared/Components/Table/CustomTextFilterCell";
import { Button } from "@progress/kendo-react-buttons";
import { Tooltip } from "@progress/kendo-react-tooltip";
import { downloadIcon } from "@progress/kendo-svg-icons";
import { StateEventsReadDto } from "../../../../Models/State/StateEventsReadDto";

interface Props {
  events: StateEventsReadDto[];
  endpointSerialNo?: string;
}

interface Page {
  skip: number;
  take: number;
}

const page: Page = { skip: 0, take: 30 };

export default function EndpointGreasePumpEventsTable(props: Props): ReactElement {
  const localizationService = useLocalization();
  const initialSort: Array<SortDescriptor> = [{ field: "timestamp", dir: "desc" }];
  const [sort] = React.useState(initialSort);
  const _export = React.useRef<ExcelExport | null>(null);
  const excelExport = () => {
    if (_export.current !== null) {
      _export.current.save();
    }
  };

  const onLabel = localizationService.toLanguageString('custom.on', enMessages.custom.on);
  const offLabel = localizationService.toLanguageString('custom.off', enMessages.custom.off);

  //Creating dataState to prevent issues with virtual scrolling
  const createAppState = (dataState: State) => {
    return {
      result: process(props.events, dataState),
      dataState: dataState,
    };
  };

  const [dataState, setDataState] = useState<any>(
    createAppState({
      skip: page.skip,
      take: page.take,
      sort: sort
    })
  );

  const dataStateChange = (event) => {
    setDataState(createAppState(event.dataState));
  };

  useEffect(() => {
    setDataState(createAppState({
      skip: page.skip,
      take: page.take,
      sort: sort
    }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props]);

  //Formating Cells
  const OccurredCell = (props: any) => {
    return (
      <td>
        <div>{moment(props.dataItem.timestamp).format('DD/MM/YYYY @ HH:mm:ss')}</div>
      </td>
    );
  }

  const UploadedCell = (props: any) => {
    return (
      <td>
        <div>{moment(props.dataItem.synced).format('DD/MM/YYYY @ HH:mm:ss')}</div>
      </td>
    );
  }

  const StateCell = (props: any) => {
    return (
      <td>
        <div>{props.dataItem.value === 1 ? onLabel : offLabel}</div>
      </td>
    );
  }

  const exportData = useMemo(() => {
    return props.events.map((item) => ({
      ...item,
      timestampExport: moment(item.timestamp).format('DD/MM/YYYY HH:mm:ss'),
      syncedExport: moment(item.synced).format('DD/MM/YYYY HH:mm:ss'),
      stateExport: item.value === 1 ? onLabel : offLabel,
    }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.events]);

  const getExcelExportData = () => {
    return process(exportData, { filter: dataState.dataState.filter, sort: dataState.dataState.sort }).data;
  };

  return (
    <div>
      <ExcelExport data={getExcelExportData()} ref={_export} collapsible={true} fileName={`Grease Pump Events - ${props.endpointSerialNo !== undefined ? props.endpointSerialNo : props.events[0]?.serialNumber}.xlsx`}>
        <ExcelExportColumn field="timestampExport" title={`${localizationService.toLanguageString('custom.occurred', enMessages.custom.occurred)}`} width={150} />
        <ExcelExportColumn field="syncedExport" title={`${localizationService.toLanguageString('custom.uploaded', enMessages.custom.uploaded)}`} width={150} />
        <ExcelExportColumn field="serialNumber" title={`${localizationService.toLanguageString('custom.serialNumber', enMessages.custom.serialNumber)}`} />
        <ExcelExportColumn field="channel" title={localizationService.toLanguageString('custom.channel', enMessages.custom.channel)} />
        <ExcelExportColumn field="stateExport" title={localizationService.toLanguageString('custom.state', enMessages.custom.state)} />
        <ExcelExportColumn field="firmwareVersion" title={localizationService.toLanguageString('custom.firmwareVersion', enMessages.custom.firmwareVersion)} />
      </ExcelExport>
      <Grid
        key={props.events.length.toString()}
        rowHeight={46}
        take={page.take}
        data={dataState.result}
        scrollable={"virtual"}
        filterable={true}
        onDataStateChange={dataStateChange}
        {...dataState.dataState}
        sortable={true}
        style={{
          height: `600px`,
        }}
        className=" w-full z-0 extra-filter-button "
      >
        <GridToolbar>
          <div style={{ width: '100%', display: 'flex', justifyContent: 'flex-end' }}>
            <Tooltip anchorElement="target" position="bottom">
              <Button
                title={localizationService.toLanguageString('custom.exportToExcel', enMessages.custom.exportToExcel)}
                svgIcon={downloadIcon}
                disabled={dataState.result.data.length === 0}
                onClick={excelExport}
              />
            </Tooltip>
          </div>
        </GridToolbar>
        <Column field="timestamp" title={`${localizationService.toLanguageString('custom.occurred', enMessages.custom.occurred)}`} cell={OccurredCell} filterable={false} />
        <Column field="synced" title={`${localizationService.toLanguageString('custom.uploaded', enMessages.custom.uploaded)}`} cell={UploadedCell} filterable={false} />
        <Column field="serialNumber" title={`${localizationService.toLanguageString('custom.serialNumber', enMessages.custom.serialNumber)}`} filterCell={CustomTextFilterCell} />
        <Column field="channel" title={localizationService.toLanguageString('custom.channel', enMessages.custom.channel)} filterCell={CustomTextFilterCell} />
        <Column field="value" title={localizationService.toLanguageString('custom.state', enMessages.custom.state)} cell={StateCell} filterable={false} />
        <Column field="firmwareVersion" title={localizationService.toLanguageString('custom.firmwareVersion', enMessages.custom.firmwareVersion)} filterCell={CustomTextFilterCell} />
      </Grid>
    </div>
  );
}
